import "./styles/levelLoading.css";
import { BalanceData, Grid } from "@highmarch/domain";
import { chapterOf } from "./chapters";
import { barHtml, btnHtml, motionOk } from "./kit";
import { portraitUrl, uiUrl } from "./portraits";
import { wipeActive } from "./transition";
import { WarMap } from "./warMap";

/**
 * The level-load panel on the closed clouds (owner report: on 4G through the tunnel PLAY "showed the loading screen
 * then took me back here"). A level's own models are tens of MB; the clouds used to part after 5 s whatever the load
 * was doing, over the still-live hub, so PLAY looked like it had failed, and a second tap started the load over.
 * Now the clouds stay closed until the level is built (transition.ts `hold`) and this panel says what is happening:
 * a progress bar, a "slow connection" note when it drags, CANCEL when it drags on, and RETRY / BACK when files failed.
 * The card keeps one size while it loads (UI design system v2, L4): the note joins the percentage's line and CANCEL
 * takes the tip's place, so nothing grows the centred card and moves the crest, the title and the bar.
 */
export const LevelLoadTiming = {
  /** A start that is ready sooner shows only the clouds: a cached level must not flash a panel. */
  showAfterMs: 600,
  /** Long enough that the player wonders: say it is the connection, not the game. */
  slowAfterMs: 8000,
  /** Long enough that the player may rather do something else: offer the way back to the hub. */
  cancelAfterMs: 15000,
} as const;

export type LoadStage = "hidden" | "shown" | "slow" | "cancellable";

/** What the panel shows `elapsedMs` into a load. */
export function loadStage(elapsedMs: number, t: { showAfterMs: number; slowAfterMs: number; cancelAfterMs: number } = LevelLoadTiming): LoadStage {
  if (elapsedMs >= t.cancelAfterMs) return "cancellable";
  if (elapsedMs >= t.slowAfterMs) return "slow";
  if (elapsedMs >= t.showAfterMs) return "shown";
  return "hidden";
}

/**
 * One level start at a time: a second PLAY / CONTINUE / map tap while the first is still asking, loading or building
 * is ignored. Each tap used to start a new request that superseded the one before it, so a player who kept tapping
 * through a slow load never got a level at all.
 */
export class StartGuard {
  private busy = false;
  get pending(): boolean { return this.busy; }

  /** Runs `task` unless a start is still pending; false (and `task` untouched) when one is. */
  run(task: () => unknown): boolean {
    if (this.busy) return false;
    this.busy = true;
    const done = () => { this.busy = false; };
    try {
      Promise.resolve(task()).then(done, done);
    } catch (err) {
      done();
      throw err;
    }
    return true;
  }
}

export type LoadFailChoice = "retry" | "back" | "anyway";
export type LoadOutcome = "ready" | "back";

/** What `runLevelLoad` needs of the panel (LevelLoading; a fake in the tests). */
export interface LoadPanel {
  /** Settles when the player taps CANCEL. */
  readonly cancelled: Promise<void>;
  /** Shows the error state for `failed` files and resolves with the player's choice. */
  fail(failed: number): Promise<LoadFailChoice>;
  /** Back to the progress state for another attempt. */
  retrying(): void;
}

/**
 * Loads a level's files until they are all in, or the player gives up. `load` resolves with the files that failed
 * (a throw counts as a failure). CANCEL wins over a load still running ("back": the caller returns to the hub and
 * drops the load's result); failed files offer RETRY (load again: finished files are cached, failed ones retried),
 * BACK, or PLAY ANYWAY (the failed models are simply not drawn: there is no stand-in kit since 2026-10-14).
 */
export async function runLevelLoad(load: () => Promise<readonly string[]>, panel: LoadPanel): Promise<LoadOutcome> {
  const cancel = panel.cancelled.then(() => null);
  for (;;) {
    const attempt = load().then((failed) => failed.length, (err: unknown) => { console.warn("level load failed", err); return 1; });
    const failed = await Promise.race([attempt, cancel]);
    if (failed === null) return "back";
    if (failed === 0) return "ready";
    const choice = await Promise.race([panel.fail(failed), cancel.then((): LoadFailChoice => "back")]);
    if (choice === "back") return "back";
    if (choice === "anyway") return "ready";
    panel.retrying();
  }
}

/** How often the panel re-reads the load's progress (the war map animates every frame in between). */
const ProgressPollMs = 250;
/** How long each of Captain Bram's tips stays before the next. */
const TipMs = 4200;
/** Longest the finished road may hold the clouds for its arrival at the gate (`finish`). */
const ArriveMaxMs = 900;

/**
 * One line of advice under the bar while a load keeps the clouds closed: something to read instead of watching a bar.
 * Each is the captain's own Guide copy (guide/topics.ts), true on every level, so no level can show a tip that does
 * not apply to it yet.
 */
export const LOAD_TIPS: readonly string[] = [
  "A longer road keeps the horde under fire for longer.",
  "Tap any tower to see how it's faring.",
  "Bad hand? REROLL deals fresh cards. You earn one every 5 waves.",
  "Misplaced a card? UNDO takes it back until the first wave.",
  "Tap a raider to learn what it is and what beats it.",
  "Three stars on every level. The battle card says what each one asks.",
  "Drag to look around, pinch to zoom, arrows to turn the map.",
  "Later levels bite hard. Replay battles for chests full of gear.",
];

/** The tip for the `n`th load of the session: each load shows the next one, round the list. */
export function loadTip(n: number): string { return LOAD_TIPS[((n % LOAD_TIPS.length) + LOAD_TIPS.length) % LOAD_TIPS.length]; }
let loadsShown = 0;

/** What kind of run is loading: the war map's heading says it in the run's own words (as the HUD title does). */
export type LoadRunKind = "campaign" | "iron" | "endless" | "daily" | "challenge" | "resume";

/** What the war map shows: the level's ground, its name and chapter, its boss, the towers the player owns. */
export interface LoadAbout {
  readonly balance: BalanceData;
  readonly level: number;
  readonly kind: LoadRunKind;
  readonly owned: readonly boolean[];
}

/** The heading over the map: a kicker line and the title, in the run's own words. Pure, for the tests. */
export function loadHeading(about: { level: number; kind: LoadRunKind; name: string; chapter: number; chapterName: string }): { kicker: string; title: string } {
  const chapter = `Chapter ${about.chapter} · ${about.chapterName}`;
  switch (about.kind) {
    case "iron": return { kicker: "Iron challenge · one try today", title: `Iron · Level ${about.level}` };
    case "endless": return { kicker: "Endless · how far can you hold?", title: `Endless · ${about.name}` };
    case "daily": return { kicker: "Daily Road · one road for every Commander", title: `Daily Road · ${about.name}` };
    case "challenge": return { kicker: "Challenge code · the same battle for everyone", title: `Challenge · ${about.name}` };
    case "resume": return { kicker: "Back to the battle", title: `Level ${about.level} · ${about.name}` };
    default: return { kicker: chapter, title: `Level ${about.level} · ${about.name}` };
  }
}

/**
 * The panel itself: the war map (ui/warMap.ts) on a parchment card over the closed clouds — the level's name over
 * its chapter's picture, the road building itself toward the fortress as the files come in, the boss that waits at
 * the end of it, and Captain Bram with a tip that changes while you wait. It sits above the clouds (their overlay
 * already swallows taps around it); where no clouds close (a browser without Web Animations), it brings its own sky
 * backdrop so the old screen is covered and cannot be tapped. `measure` reports progress in whatever units the caller
 * counts (models, plus one for the ground). Without `about` (a caller that knows no level) the card has no map.
 */
export class LevelLoading implements LoadPanel {
  readonly cancelled: Promise<void>;
  private readonly root: HTMLDivElement;
  private readonly timers: ReturnType<typeof setTimeout>[] = [];
  private readonly poll: ReturnType<typeof setInterval>;
  private readonly tipTimer: ReturnType<typeof setInterval>;
  private cancel: () => void = () => undefined;
  private choose: ((c: LoadFailChoice) => void) | null = null;
  private readonly map: WarMap | null = null;
  private target = 0;
  private done = false;
  private raf = 0;
  private last = 0;
  private tip: number;

  constructor(private readonly measure: () => { done: number; total: number }, about: LoadAbout | null = null) {
    this.cancelled = new Promise<void>((res) => { this.cancel = res; });
    this.root = document.createElement("div");
    const calm = !motionOk();
    this.root.className = `lvload${wipeActive() ? "" : " bare"}${about ? " mapped" : ""}${calm ? " calm" : ""}`;
    this.tip = loadsShown++;
    const B = about?.balance;
    const L = about && B ? B.level(about.level) : null;
    const chapter = about && B ? chapterOf(B, about.level, B.levels.length) : null;
    const heading = about && L && chapter ? loadHeading({ level: about.level, kind: about.kind, name: L.name, chapter: chapter.index + 1, chapterName: chapter.name }) : null;
    const boss = L && B ? B.enemies[L.bossEnemyIndex] : null;
    const bossIcon = boss ? portraitUrl(boss.id) : null;
    this.root.innerHTML = `
      <div class="panel">
        <img class="crest" src="${uiUrl("brand/emblem_256.png")}" alt="">
        ${heading && chapter ? `<div class="wm-head" style="background-image:linear-gradient(180deg, rgba(20,16,30,0.15), rgba(20,16,30,0.72)), url('${uiUrl(`chapters/${chapter.biome}_intro@2x.jpg`)}')">
          <small class="kicker"></small><b class="disp name"></b></div>` : ""}
        ${about ? `<div class="wm-map"><div class="wm-paper"></div>${boss && bossIcon ? `<div class="wm-boss"><div class="ring"><img src="${bossIcon}" alt=""></div><small>BOSS</small><b></b></div>` : ""}</div>` : ""}
        <div class="progress">
          <div class="head disp">${about ? "Marching to battle" : "Readying the battle"}</div>
          ${about ? "" : barHtml(0, "gold")}
          <div class="count"><span class="pct">…</span><span class="slow"> · slow connection</span></div>
        </div>
        <div class="error">
          <div class="head disp">The road is blocked</div>
          <div class="why"></div>
          ${btnHtml("RETRY", "gold", { className: "retry" })}
          ${btnHtml("BACK", "dark", { className: "back" })}
          <button class="anyway">Fight on with plain models</button>
        </div>
        <div class="foot">
          <div class="bram"><img src="${uiUrl("captain/bram_pointing@1x.png")}" alt=""><p class="tip"><b>Captain Bram</b><span></span></p></div>
          ${btnHtml("CANCEL", "dark", { className: "cancel" })}
        </div>
      </div>`;
    if (heading) { this.root.querySelector(".wm-head .kicker")!.textContent = heading.kicker; this.root.querySelector(".wm-head .name")!.textContent = heading.title; }
    if (boss) { const b = this.root.querySelector(".wm-boss b"); if (b) b.textContent = boss.name; }
    this.root.querySelector(".tip span")!.textContent = loadTip(this.tip);
    const on = (sel: string, f: () => void) => { this.root.querySelector<HTMLElement>(sel)!.onclick = f; };
    on(".cancel", () => this.cancel());
    on(".retry", () => this.pick("retry"));
    on(".back", () => this.pick("back"));
    on(".anyway", () => this.pick("anyway"));
    if (about && B) {
      const towerIcons = B.towers.filter((t) => !t.isBase && about.owned[t.index]).map((t) => portraitUrl(t.id, 2)).filter((u): u is string => u !== null);
      this.map = new WarMap({ grid: Grid.fromLevel(B.level(about.level)), seed: about.level, towerIcons, fortressIcon: portraitUrl("base", 2), calm });
      this.root.querySelector(".wm-map")!.prepend(this.map.canvas);
    }
    // on the body, beside the clouds (transition.ts): inside #ui it would sit under them, whatever its z-index
    document.body.append(this.root);
    if (this.map) this.map.fit(Math.min(innerWidth - 32, 340) - 44, Math.min(250, innerHeight * 0.3));
    this.restartClock();
    this.poll = setInterval(() => this.paint(), ProgressPollMs);
    this.tipTimer = setInterval(() => this.nextTip(), TipMs);
    this.paint();
  }

  fail(failed: number): Promise<LoadFailChoice> {
    this.clearTimers();
    this.root.classList.remove("slow", "cancellable");
    this.root.classList.add("on", "failed");
    this.root.querySelector(".why")!.textContent = `${failed === 1 ? "A piece" : `${failed} pieces`} of the battlefield went missing on the way. Check your connection and try again.`;
    return new Promise((res) => { this.choose = res; });
  }

  retrying(): void {
    this.root.classList.remove("failed");
    this.restartClock();
  }

  /**
   * The load is in. If the panel is up, the road runs the rest of the way to the gate and the fortress answers
   * (at most `ArriveMaxMs`) before the clouds part; a panel that never showed (a quick, cached start) settles at once.
   */
  async finish(): Promise<void> {
    this.done = true;
    this.paint();
    if (!this.map || !this.root.classList.contains("on") || this.root.classList.contains("failed")) return;
    const t0 = performance.now();
    while (!this.map.arrived && performance.now() - t0 < ArriveMaxMs) await new Promise((r) => setTimeout(r, 50));
    this.root.classList.add("arrived");
    await new Promise((r) => setTimeout(r, Math.max(0, Math.min(380, ArriveMaxMs - (performance.now() - t0)))));
  }

  dispose(): void {
    this.clearTimers();
    clearInterval(this.poll);
    clearInterval(this.tipTimer);
    cancelAnimationFrame(this.raf);
    this.raf = 0;
    this.root.remove();
  }

  private pick(choice: LoadFailChoice): void {
    const f = this.choose;
    this.choose = null;
    f?.(choice);
  }

  /** Each attempt gets the full grace again before "slow" and CANCEL show: a RETRY is a fresh start. */
  private restartClock(): void {
    this.clearTimers();
    this.root.classList.remove("slow", "cancellable");
    const stageAt = (ms: number) => this.timers.push(setTimeout(() => this.setStage(loadStage(ms)), ms));
    stageAt(LevelLoadTiming.showAfterMs);
    stageAt(LevelLoadTiming.slowAfterMs);
    stageAt(LevelLoadTiming.cancelAfterMs);
  }

  private setStage(stage: LoadStage): void {
    this.root.classList.toggle("on", stage !== "hidden");
    this.root.classList.toggle("slow", stage === "slow" || stage === "cancellable");
    this.root.classList.toggle("cancellable", stage === "cancellable");
    // the map draws only while it can be seen
    if (stage !== "hidden" && this.map && !this.raf) { this.last = performance.now(); this.raf = requestAnimationFrame(this.frame); }
  }

  private readonly frame = (now: number): void => {
    this.raf = requestAnimationFrame(this.frame);
    const dt = Math.min(0.1, (now - this.last) / 1000);
    this.last = now;
    this.map?.step(dt, this.target, this.done);
  };

  private clearTimers(): void {
    for (const t of this.timers.splice(0)) clearTimeout(t);
  }

  private nextTip(): void {
    const el = this.root.querySelector<HTMLElement>(".tip span");
    if (!el || !this.root.classList.contains("on")) return;
    this.tip++;
    el.classList.remove("tip-in");
    void el.offsetWidth;                                      // restarts the fade-in
    el.textContent = loadTip(this.tip);
    el.classList.add("tip-in");
  }

  private paint(): void {
    const { done, total } = this.measure();
    this.target = this.done ? 1 : total > 0 ? Math.min(1, done / total) : 0;
    const fill = this.root.querySelector<HTMLElement>(".bar .fill");
    if (fill) fill.style.width = `${this.target * 100}%`;
    // a percentage, not "32 / 77": the player cares how far along it is, not how many files a level has
    this.root.querySelector(".count .pct")!.textContent = total > 0 || this.done ? `${Math.round(this.target * 100)}%` : "…";
  }
}
